import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { api, fmtErr } from "../../../lib/api";
import { limitToast } from "../../../lib/limits";
import { StatusBadge } from "../Dashboard";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog";
import { Trash2, Pencil, Ban, AlertTriangle, Send } from "lucide-react";
import { startsAfterDeadline } from "./TaskGroup";
import { AuditTimeline } from "../../../components/AuditTimeline";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../../components/ui/dialog";
import { Textarea } from "../../../components/ui/textarea";
import { toast } from "sonner";
import { INTERNAL_STATUS, VENDOR_STATUS, PROVIDER_FORBIDDEN_STATUSES, CREW_VENDOR_HIDDEN_STATUSES, NO_CANCEL_STATUSES, ONLINE_OWNER_ALLOWED } from "./constants";
import { Row, PriorityPill, ModalTab, PhotoBucket, DocumentsBucket, VatBreakdown, ShowInvoiceButton } from "./components";
import FileUploader from "../../../components/FileUploader";
import { FindSupplierDialog } from "./FindSupplierPicker";
import { TaskQuotes } from "./TaskQuotes";
import { TaskReviewBlock } from "../../../components/SupplierReview";

// Owner ↔ accepted-supplier message thread, shown inline in the Details tab.
function OwnerQuoteThread({ quote, onChanged }) {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!msg.trim() || busy) return;
    setBusy(true);
    try {
      await api.post(`/quotes/${quote.id}/messages`, { text: msg.trim() });
      setMsg("");
      await onChanged?.();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setBusy(false); }
  };
  return (
    <div>
      <div className="space-y-2.5 max-h-64 overflow-y-auto pr-1">
        {(quote.messages || []).length === 0 && (
          <p className="text-xs text-zinc-400 text-center py-2">No messages yet — start the conversation with the supplier.</p>
        )}
        {(quote.messages || []).map((m) => {
          const mine = m.sender_kind === "requester";
          return (
            <div key={m.id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`}>
              <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-xs ${mine ? "bg-ink text-white rounded-br-sm" : "bg-zinc-100 text-zinc-800 rounded-bl-sm"}`}>
                <div className={`text-[10px] mb-0.5 font-medium ${mine ? "text-zinc-300" : "text-zinc-500"}`}>{m.sender_name}</div>
                {m.deleted
                  ? <div className="italic opacity-60">Message removed</div>
                  : <div className="whitespace-pre-wrap break-words">{m.text}</div>}
              </div>
              <div className="text-[10px] text-zinc-400 mt-0.5 px-1">{(m.at || "").slice(0, 16).replace("T", " ")}</div>
            </div>
          );
        })}
      </div>
      <div className="flex gap-2 mt-2">
        <Input value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Write a message…" onKeyDown={(e) => e.key === "Enter" && send()} data-testid="job-detail-msg-input" />
        <Button size="icon" className="bg-ink hover:bg-black shrink-0" onClick={send} disabled={busy} data-testid="job-detail-msg-send"><Send className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}

export function JobDetailModal({ job, inventory, members, userId, userRole, can, onClose, onRefresh, onDelete, onEdit, initialTab = "details", deleteLabel = "Delete job" }) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(job);
  // Accepted quote (for the supplier's scope in the Details tab).
  const [acceptedQuote, setAcceptedQuote] = useState(null);
  const loadAcceptedQuote = useCallback(async () => {
    const qid = current.assigned_supplier?.quote_id;
    if (!qid) { setAcceptedQuote(null); return; }
    try { const { data } = await api.get(`/quotes/${qid}`); setAcceptedQuote(data); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current.assigned_supplier?.quote_id]);
  useEffect(() => { loadAcceptedQuote(); }, [loadAcceptedQuote]);
  // "quotes-expand" = opened from a quote/messages badge in the list — show
  // the quotes tab AND auto-expand the message threads to the newest message.
  const [tab, setTab] = useState(initialTab === "quotes-expand" ? "quotes" : initialTab);
  const [auditRows, setAuditRows] = useState(null);
  // "Find supplier" dialog for an existing task (request quotes). quotesKey
  // remounts the Supplier-quotes list after new invites are sent.
  const [findSup, setFindSup] = useState(false);
  const [quotesKey, setQuotesKey] = useState(0);
  const canManageQuotes = ["owner", "manager", "captain"].includes(userRole);
  // Suppliers (provider role) must NEVER see the Supplier-quotes tab — it
  // lists competing suppliers' quotes. Blocked here AND server-side (403 on
  // GET /maintenance/{id}/quotes for providers).
  const isProviderUser = userRole === "provider";
  // Confirmation gate before the destructive delete fires — guards
  // against accidental taps on the red button.
  const [confirmDel, setConfirmDel] = useState(false);
  const [deleting, setDeleting] = useState(false);
  // Cancel-accepted-job flow: reason + reopen-vs-cancel choice dialog.
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelling, setCancelling] = useState(null);

  // The dashboard / list views cache a stale snapshot of the job (the
  // status the page saw when it loaded). If a senior crew member has
  // since changed the status the modal would show the wrong dropdown
  // value until manually refreshed. Refetch the job on open so the
  // status pill / dropdown / docs always reflect the latest server
  // state. Re-runs whenever a different job is opened.
  useEffect(() => {
    let alive = true;
    api.get(`/maintenance/${job.id}`).then(({ data }) => {
      if (alive && data) setCurrent(data);
    }).catch(() => { /* fall back to the cached `job` prop */ });
    return () => { alive = false; };
  }, [job.id]);

  const assignee = members.find((m) => (m.user_id || m.id) === current.assigned_to);
  const isAssignee = current.assigned_to && current.assigned_to === userId;
  const isAssignedProvider = assignee?.role === "provider" && isAssignee;
  const isVendorJob = isAssignedProvider || !!(current.external_supplier && current.external_supplier.supplier_name) || !!(current.assigned_supplier);
  // Fully online managed job — accepted marketplace quote drives the flow.
  const onlineJob = !!current.assigned_supplier?.quote_id;
  // After-photo upload + status edit are both gated on (maintenance:edit OR is-assignee).
  const canEdit = can("maintenance:edit") || isAssignee;
  const canAddAfter = can("maintenance:edit") || isAssignedProvider || isAssignee;

  // Soft-delete state: deleted rows are read-only audit views.
  const isDeleted = !!current.deleted_at;

  // "Edit task" permission check (frontend mirror of the backend rule):
  //  • creator can always edit; OR
  //  • caller has maintenance:edit AND outranks the creator on this yacht; OR
  //  • caller has maintenance:edit AND the job is still in-flight (not
  //    confirmed/cancelled) — added 2026-05-31 so any senior can reassign
  //    an open task without owning the original ticket.
  // The creator's current rank is looked up from `members`; if they're
  // no longer a member, fall back to "deckhand" so a senior can clean up.
  const ROLE_RANK = { owner: 100, manager: 75, captain: 50, engineer: 20, accountant: 20, deckhand: 10, provider: 10 };
  const isCreator = current.created_by && current.created_by === userId;
  const creatorMember = members.find((m) => (m.user_id || m.id) === current.created_by);
  const creatorRole = creatorMember?.role || "deckhand";
  const outranksCreator = (ROLE_RANK[userRole] || 0) > (ROLE_RANK[creatorRole] || 0);
  const FINAL_STATUSES = new Set(["confirmed", "cancelled"]);
  const isStillOpen = !FINAL_STATUSES.has(current.status || "open");
  const canEditThisJob = !isDeleted && (isVendorJob ? current.status === "open" : true) && (
    isCreator
    || (can("maintenance:edit") && (outranksCreator || isStillOpen))
  );
  // Delete: open tasks (user rule 2026-06-12) and fully-cancelled tasks
  // (user request 2026-06) — everything else is audit trail. EXCEPTION
  // (user rule 2026-06): offline-tracked / external-supplier tasks are
  // deletable at ANY status by owner/manager (captain → needs approval).
  const offlineTask = !!(current.track_offline || current.external_supplier);
  // Internal tasks (no supplier attached — user rule 2026-06): same
  // any-status senior deletion routing as offline tasks.
  const internalTask = !offlineTask && !current.assigned_supplier;
  // Assigned to an internal crew member (not a vendor/supplier flow) —
  // the Supplier quotes tab is hidden to keep the flow internal-only.
  const internalAssigned = !!current.assigned_to && !isVendorJob && !current.track_offline;
  const seniorRole = ["owner", "manager", "captain"].includes(userRole);
  const [delInvToo, setDelInvToo] = useState(false);
  const canDeleteThisJob = !isDeleted && !current.pending_deletion && (
    ((current.status === "open" || current.status === "cancelled") && (isCreator || (can("maintenance:delete") && outranksCreator)))
    || ((offlineTask || internalTask) && seniorRole)
  );
  // Accepted supplier jobs can't be deleted — they get a clean cancel path
  // instead (notify supplier + reopen or cancel the task).
  const canCancelAccepted = !isDeleted && canManageQuotes && !!current.assigned_supplier
    && !NO_CANCEL_STATUSES.has(current.status)
    && !["done", "cancelled"].includes(current.status);

  const doCancelAccepted = async (mode) => {
    if (cancelling) return;
    setCancelling(mode);
    try {
      await api.post(`/maintenance/${current.id}/cancel-accepted`, { mode, reason: cancelReason.trim() || null });
      toast.success(mode === "reopen" ? "Job cancelled — task reopened" : "Task cancelled");
      setCancelOpen(false);
      setCancelReason("");
      const { data } = await api.get(`/maintenance/${current.id}`);
      if (data) setCurrent(data);
      await onRefresh?.();
    } catch (e) { toast.error(fmtErr(e)); }
    finally { setCancelling(null); }
  };

  const addAfter = async (path) => {
    try {
      const { data } = await api.post(`/maintenance/${current.id}/photos/after`, { photos: [path] });
      setCurrent(data);
      await onRefresh?.();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const addBefore = async (path) => {
    try {
      const { data } = await api.post(`/maintenance/${current.id}/photos/before`, { photos: [path] });
      setCurrent(data);
      await onRefresh?.();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  // Inline status change — the assigned provider (and senior crew) can walk
  // the job through the workflow straight from the detail modal. Providers
  // are blocked from the senior-only statuses both here and server-side.
  const canChangeStatus = !isDeleted && (can("maintenance:edit") || isAssignee);
  const statusChoices = (() => {
    const list = isVendorJob ? VENDOR_STATUS : INTERNAL_STATUS;
    // Online jobs (user rules 2026-06): once approved, the yacht side only
    // gets the per-stage actions from ONLINE_OWNER_ALLOWED. A cancelled
    // online job is final — it can't be reactivated.
    if (onlineJob && !isAssignedProvider && current.status === "cancelled") return ["cancelled"];
    if (onlineJob && !isAssignedProvider && current.status !== "cancelled"
        && list.indexOf(current.status) >= list.indexOf("approved")) {
      const allowed = [...(ONLINE_OWNER_ALLOWED[current.status] || [])];
      // "Cancelled" stays selectable at later stages too — picking it then
      // opens the supplier-approval request instead of cancelling directly.
      if (["waiting_parts", "in_progress", "completed", "dispute"].includes(current.status)) allowed.push("cancelled");
      return [current.status, ...allowed.filter((s) => s !== current.status)];
    }
    // Vendor jobs: hide auto/senior-only statuses. Suppliers (assigned
    // provider) also can't see `confirmed`; crew keep it for sign-off.
    const hidden = isVendorJob
      ? (isAssignedProvider ? PROVIDER_FORBIDDEN_STATUSES : CREW_VENDOR_HIDDEN_STATUSES)
      : new Set();
    // Forward-only: once a vendor job reached "approved", NO earlier status
    // may be selected by anyone. "cancelled" stays available. Filtering the
    // full list keeps canonical order (current status stays at its slot).
    const curIdx = list.indexOf(current.status);
    const lockBack = isVendorJob && current.status !== "cancelled" && curIdx >= list.indexOf("approved");
    // Point of no return: prepayment done / work started ⇒ no cancelling.
    const noCancel = isVendorJob && NO_CANCEL_STATUSES.has(current.status);
    const base = list.filter((s) => {
      if (s === current.status) return true;
      if (hidden.has(s)) return false;
      if (s === "cancelled") return !noCancel;
      // Dispute is only available against work the supplier marked completed.
      if (s === "dispute" && current.status !== "completed") return false;
      // "Prepay too high" only makes sense against an open prepayment request.
      if (s === "prepay_too_high" && current.status !== "waiting_prepay") return false;
      if (lockBack && list.indexOf(s) < curIdx) return false;
      return true;
    });
    return base.includes(current.status) ? base : [current.status, ...base];
  })();
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeReason, setDisputeReason] = useState("");
  const [reworkDeadline, setReworkDeadline] = useState("");
  const [prepayTooHighOpen, setPrepayTooHighOpen] = useState(false);
  const [prepayNote, setPrepayNote] = useState("");
  const [prepayDoneOpen, setPrepayDoneOpen] = useState(false);
  const [receiptDoc, setReceiptDoc] = useState(null);
  // Online-job cancellation (user rules 2026-06): direct cancel needs a note;
  // from waiting_parts on it becomes an approval request to the supplier.
  const [cxlOpen, setCxlOpen] = useState(false);
  const [cxlNote, setCxlNote] = useState("");
  const [cxlMode, setCxlMode] = useState("direct");
  const canRequestCancel = onlineJob && !isAssignedProvider && !isDeleted && can("maintenance:edit")
    && !current.cancel_request && ["waiting_parts", "in_progress", "completed", "dispute"].includes(current.status);
  // Supplier bank details for paying the requested prepayment (2026-06).
  const [bank, setBank] = useState(null);
  const [bankOpen, setBankOpen] = useState(false);
  useEffect(() => { setBank(null); setBankOpen(false); }, [current.id]);
  const toggleBank = async () => {
    if (bankOpen) { setBankOpen(false); return; }
    if (!bank) {
      try {
        const { data } = await api.get(`/maintenance/${current.id}/supplier-bank`);
        setBank(data);
      } catch (e) { toast.error(fmtErr(e)); return; }
    }
    setBankOpen(true);
  };
  const acceptFee = async () => {
    try {
      const { data } = await api.post(`/maintenance/${current.id}/cancel-request/fee/accept`, {});
      if (data) setCurrent(data);
      toast.success("Fee accepted — job cancelled");
      await loadAcceptedQuote();
      await onRefresh?.();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const submitCxl = async () => {
    const note = cxlNote.trim();
    if (cxlMode !== "reject" && cxlMode !== "fee_decline" && !note) return;
    setCxlOpen(false);
    if (cxlMode === "direct") { await saveStatus("cancelled", note); await loadAcceptedQuote(); return; }
    try {
      const url = cxlMode === "reject"
        ? `/maintenance/${current.id}/cancel-request/reject`
        : cxlMode === "fee_decline"
        ? `/maintenance/${current.id}/cancel-request/fee/decline`
        : `/maintenance/${current.id}/cancel-request`;
      const { data } = await api.post(url, { note });
      if (data) setCurrent(data);
      toast.success(cxlMode === "reject" ? "Cancellation request rejected"
        : cxlMode === "fee_decline" ? "Fee declined — the supplier can propose a new one"
        : "Cancellation request sent to the supplier");
      await loadAcceptedQuote();
      await onRefresh?.();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const snoozeDeadline = async (days) => {
    try {
      const { data } = await api.post(`/maintenance/${current.id}/snooze-deadline-alert`, { days });
      if (data) setCurrent(data);
      toast.success(`Deadline alert snoozed for ${days} days`);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const resolveCancelReq = (action) => async () => {
    if (action === "reject") { setCxlNote(""); setCxlMode("reject"); setCxlOpen(true); return; }
    try {
      const { data } = await api.post(`/maintenance/${current.id}/cancel-request/${action}`, {});
      if (data) setCurrent(data);
      toast.success("Cancellation approved — job cancelled");
      await loadAcceptedQuote();
      await onRefresh?.();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const jobCurrency = current.assigned_supplier?.currency || "";
  const fmtPrepay = (v) => `${jobCurrency ? jobCurrency + " " : ""}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const todayStr = new Date().toISOString().slice(0, 10);
  const reworkOverdue = current.status === "dispute" &&
    (current.dispute_overdue || ((current.rework_deadline || "").slice(0, 10) && (current.rework_deadline || "").slice(0, 10) < todayStr));
  const saveStatus = async (status, note, extra = {}) => {
    try {
      const { data } = await api.patch(`/maintenance/${current.id}/status`, { status, note: note || null, ...extra });
      if (data) setCurrent(data);
      toast.success(`Status → ${t(`tasks.status_${status}`)}`);
      await onRefresh?.();
    } catch (e) {
      if (!(status === "confirmed" && limitToast(e, { yacht_id: current.yacht_id, kind: "final_approval", ref_id: current.id, label: current.title || "" }))) {
        toast.error(fmtErr(e));
      }
    }
  };
  // Hard-delete a single before/after media item (object + metadata). The
  // PhotoBucket asks for confirmation before calling these. Errors bubble so
  // the bucket can keep its dialog open / surface them.
  const deleteBefore = async (path) => {
    const { data } = await api.delete(`/maintenance/${current.id}/photos/before`, { data: { photos: [path] } });
    if (data) setCurrent(data);
    await onRefresh?.();
  };
  const deleteAfter = async (path) => {
    const { data } = await api.delete(`/maintenance/${current.id}/photos/after`, { data: { photos: [path] } });
    if (data) setCurrent(data);
    await onRefresh?.();
  };
  const addDocument = async (path, name, kind) => {
    try {
      const { data } = await api.post(`/maintenance/${current.id}/documents`, {
        file_path: path, name, kind,
      });
      setCurrent(data);
      toast.success(`${
        kind === "quotation" ? "Quotation"
        : kind === "invoice" ? "Invoice"
        : kind === "payment" ? "Payment receipt"
        : kind === "report" ? "Report"
        : "Document"
      } attached`);
      await onRefresh?.();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const removeDocument = async (docId) => {
    try {
      const { data } = await api.delete(`/maintenance/${current.id}/documents/${docId}`);
      setCurrent(data);
      await onRefresh?.();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const loadAudit = async () => {
    try {
      const { data } = await api.get(`/audit?ref_kind=maintenance&ref_id=${current.id}`);
      setAuditRows(data);
    } catch (e) {
      setAuditRows([]);
    }
  };
  // History is now an always-visible section at the bottom of Details.
  useEffect(() => {
    loadAudit();
    /* eslint-disable-next-line */
  }, [current.id]);

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="w-[calc(100vw-1rem)] sm:max-w-md max-h-[calc(100dvh-1rem)] overflow-y-auto overflow-x-hidden [&>*]:min-w-0 p-4 sm:p-6" data-testid="job-detail-modal">
        <DialogHeader>
          <DialogTitle className="text-base sm:text-lg break-words pr-6">{current.title}</DialogTitle>
        </DialogHeader>

        {["confirmed", "full_payment_confirmed"].includes(current.status) && !isDeleted && (
          <div className="-mt-1">
            <ShowInvoiceButton invoiceId={current.invoice_id} testid="job-detail-show-invoice" />
          </div>
        )}

        {current.pending_deletion && !isDeleted && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-900" data-testid="job-pending-deletion">
            <span className="font-medium">Deletion requested</span> by {current.pending_deletion.requested_by_name || "the captain"}
            {current.pending_deletion.delete_invoice ? " (incl. linked invoice)" : ""}.
            {["owner", "manager"].includes(userRole) ? (
              <span className="ml-2 inline-flex gap-3">
                <button type="button" className="underline font-semibold" data-testid="job-deletion-approve"
                  onClick={async () => {
                    try {
                      await api.post(`/deletion-requests/${current.pending_deletion.id}/approve`);
                      toast.success("Deletion approved — task deleted");
                      onClose(); await onRefresh?.();
                    } catch (e) { toast.error(fmtErr(e)); }
                  }}>Approve</button>
                <button type="button" className="underline" data-testid="job-deletion-reject"
                  onClick={async () => {
                    try {
                      await api.post(`/deletion-requests/${current.pending_deletion.id}/reject`);
                      toast.success("Deletion rejected");
                      const { data } = await api.get(`/maintenance/${current.id}`);
                      if (data) setCurrent(data);
                      await onRefresh?.();
                    } catch (e) { toast.error(fmtErr(e)); }
                  }}>Reject</button>
              </span>
            ) : (
              <span className="ml-1 text-red-700">Awaiting owner/manager approval.</span>
            )}
          </div>
        )}

        {current.cancel_request && !isDeleted && (
          <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900" data-testid="job-cancel-request-banner">
            {current.cancel_request.requested_by_kind === "supplier" ? (
              <>
                <span className="font-medium">Supplier requests cancellation</span>
                {current.cancel_request.requested_by_name ? ` (${current.cancel_request.requested_by_name})` : ""}: “{current.cancel_request.note}”
                {!isProviderUser && can("maintenance:edit") && (
                  <span className="ml-2 inline-flex gap-3">
                    <button type="button" className="underline font-semibold" data-testid="job-cancel-req-approve" onClick={resolveCancelReq("approve")}>Approve</button>
                    <button type="button" className="underline" data-testid="job-cancel-req-reject" onClick={resolveCancelReq("reject")}>Reject</button>
                  </span>
                )}
              </>
            ) : current.cancel_request.state === "fee_proposed" ? (
              <>
                <span className="font-medium">Supplier agrees to cancel — fee proposed:</span>{" "}
                {current.cancel_request.fee_currency} {Number(current.cancel_request.fee_amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}.
                {" "}Accept the fee to cancel the job, or decline it to keep negotiating.
                {!isProviderUser && can("maintenance:edit") && (
                  <span className="ml-2 inline-flex gap-3">
                    <button type="button" className="underline font-semibold" data-testid="job-fee-accept" onClick={acceptFee}>Accept fee & cancel</button>
                    <button type="button" className="underline" data-testid="job-fee-decline" onClick={() => { setCxlNote(""); setCxlMode("fee_decline"); setCxlOpen(true); }}>Decline fee</button>
                  </span>
                )}
              </>
            ) : (
              <>
                <span className="font-medium">Cancellation requested</span> — waiting for the supplier's approval. Note: “{current.cancel_request.note}”
              </>
            )}
          </div>
        )}

        <Dialog open={cxlOpen} onOpenChange={setCxlOpen}>
          <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-sm" data-testid="job-cancel-note-dialog">
            <DialogHeader><DialogTitle>{cxlMode === "direct" ? "Cancel this job" : cxlMode === "reject" ? "Reject cancellation request" : cxlMode === "fee_decline" ? "Decline the cancellation fee" : "Request cancellation"}</DialogTitle></DialogHeader>
            <p className="text-sm text-zinc-600">
              {cxlMode === "direct"
                ? "The job will be cancelled and the supplier informed. Add a short note to the supplier explaining why."
                : cxlMode === "reject"
                ? "The job stays active. You can add an optional note to the supplier explaining why."
                : cxlMode === "fee_decline"
                ? "The fee goes back to the supplier — they can propose a different fee, cancel without one, or reject your request so the job continues. Optional note:"
                : "Work has already started — cancelling now needs the supplier's approval. Add a note explaining why."}
            </p>
            <Textarea value={cxlNote} onChange={(e) => setCxlNote(e.target.value)} placeholder="Note to the supplier…" rows={3} data-testid="job-cancel-note-input" />
            <DialogFooter>
              <Button variant="outline" onClick={() => setCxlOpen(false)} data-testid="job-cancel-note-back">Back</Button>
              <Button className="bg-rose-600 hover:bg-rose-700 text-white" disabled={!["reject", "fee_decline"].includes(cxlMode) && !cxlNote.trim()} onClick={submitCxl} data-testid="job-cancel-note-submit">
                {cxlMode === "direct" ? "Cancel job" : cxlMode === "reject" ? "Reject request" : cxlMode === "fee_decline" ? "Decline fee" : "Send request"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="flex gap-1 border-b border-zinc-200 -mx-4 sm:-mx-6 px-4 sm:px-6">
          <ModalTab active={tab === "details"} onClick={() => setTab("details")} testid="job-tab-details">
            Details
          </ModalTab>
          {!isProviderUser && !internalAssigned && (
            <ModalTab active={tab === "quotes"} onClick={() => setTab("quotes")} testid="job-tab-quotes">
              Supplier quotes
            </ModalTab>
          )}
        </div>

        {tab === "quotes" && !isProviderUser && !internalAssigned ? (
          <>
            {current.description && (
              <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50/60 p-3" data-testid="job-quotes-task-description">
                <div className="text-[11px] uppercase tracking-wider text-amber-800 font-semibold mb-1">Task description</div>
                <div className="text-sm text-zinc-800 whitespace-pre-wrap">{current.description}</div>
              </div>
            )}
            <TaskQuotes key={quotesKey} jobId={current.id} jobStatus={current.status} canManage={canManageQuotes} onFindSupplier={() => setFindSup(true)} autoExpand={initialTab === "quotes-expand"} />
          </>
        ) : (
        <>
        {/* First block — supplier name · task name + review (invoice-style, spec 2026-06) */}
        <div className="mt-4">
          <div className="text-base font-semibold" data-testid="job-detail-supplier-line">
            {assignee ? (
              assignee.company ? (
                <>
                  <span className="block">{assignee.company}</span>
                  <span className="block text-xs font-normal text-zinc-500">{assignee.name || assignee.email}</span>
                </>
              ) : (assignee.name || assignee.email)
            ) : current.external_supplier?.supplier_name ? (
              <>
                {current.external_supplier.supplier_name}
                <span className="text-zinc-400 text-xs font-normal ml-1">· external (offline)</span>
              </>
            ) : current.assigned_supplier?.supplier_name ? (
              current.assigned_supplier.supplier_name
            ) : "Unassigned"}
            {current.assigned_supplier?.contact_person?.name && (
              <span className="flex items-center gap-1.5 text-xs font-normal text-zinc-500 mt-0.5" data-testid="job-detail-contact-person">
                {current.assigned_supplier.contact_person.picture && (
                  <img src={current.assigned_supplier.contact_person.picture} alt="" className="h-5 w-5 rounded-full object-cover" />
                )}
                Your contact: {current.assigned_supplier.contact_person.name}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 flex-wrap mt-1" data-testid="job-detail-title-row">
            <span className="text-sm font-medium text-zinc-900">{current.title}</span>
            {["completed", "done", "confirmed"].includes(current.status) && (
              <TaskReviewBlock taskId={current.id} canReview={!isProviderUser && can("maintenance:edit")} />
            )}
          </div>
          {current.track_offline && (
            <span className="mt-1.5 inline-flex items-center rounded-full border border-zinc-300 bg-zinc-100 px-2 py-0.5 text-xs text-zinc-600" data-testid="job-track-offline-badge">
              Tracked offline — supplier receives no notifications
            </span>
          )}
        </div>

        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm mt-2">
          <Row
            label={(current.system_categories && current.system_categories.length > 1) ? "Categories" : "Category"}
            value={
              <span className="inline-flex items-center gap-2 flex-wrap">
                {(current.system_categories && current.system_categories.length)
                  ? current.system_categories.map((c) => (
                      <span key={c} className="inline-flex rounded-full border border-zinc-300 bg-zinc-50 px-2 py-0.5 text-xs">{c}</span>
                    ))
                  : (current.system_category || "—")}
              </span>
            }
          />
          <Row
            label="Status"
            value={current.status !== "confirmed" && canChangeStatus ? (
              <select
                value={current.status}
                onChange={(e) => {
                  if (e.target.value === "dispute") { setDisputeReason(""); setReworkDeadline(new Date(Date.now() + 7 * 86400000).toISOString().slice(0, 10)); setDisputeOpen(true); e.target.value = current.status; }
                  else if (e.target.value === "prepay_too_high") { setPrepayNote(""); setPrepayTooHighOpen(true); e.target.value = current.status; }
                  else if (e.target.value === "prepay_done" && current.prepay_amount != null) { setReceiptDoc(null); setPrepayDoneOpen(true); e.target.value = current.status; }
                  else if (e.target.value === "cancelled" && onlineJob) {
                    const needsApproval = ["waiting_parts", "in_progress", "completed", "dispute"].includes(current.status);
                    if (needsApproval && current.cancel_request) { toast.info("A cancellation request is already pending."); }
                    else { setCxlNote(""); setCxlMode(needsApproval ? "request" : "direct"); setCxlOpen(true); }
                    e.target.value = current.status;
                  }
                  else saveStatus(e.target.value);
                }}
                className="h-8 rounded-md border border-input px-2 text-sm bg-white"
                data-testid="job-detail-status-select"
              >
                {statusChoices.map((s) => (
                  <option key={s} value={s}>{t(`tasks.status_${s}`)}</option>
                ))}
              </select>
            ) : (
              <StatusBadge value={current.status} />
            )}
          />
          {(() => {
            // Deadline-at-risk banner + one-tap snooze (user request 2026-06).
            if (!onlineJob || isAssignedProvider || !canChangeStatus) return null;
            if (!["approved", "waiting_prepay", "prepay_too_high", "prepay_done"].includes(current.status)) return null;
            const due = (current.due_date || "").slice(0, 10);
            if (!due) return null;
            const days = Math.round((new Date(due) - new Date(todayStr)) / 86400000);
            if (days > 3) return null;
            if ((current.start_warn_snoozed_until || "") > todayStr) return null;
            return (
              <div className="sm:col-span-2 rounded-md border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2 text-xs" data-testid="job-deadline-risk-banner">
                <span className="font-semibold">Deadline at risk:</span> the deadline ({due}){" "}
                {days < 0 ? "has passed" : days === 0 ? "is today" : `is in ${days} day${days === 1 ? "" : "s"}`} and the supplier hasn't started the work yet.
                <span className="ml-2 inline-flex gap-2 whitespace-nowrap">
                  {[3, 7].map((d) => (
                    <button key={d} type="button" className="underline font-semibold" data-testid={`job-snooze-${d}d`} onClick={() => snoozeDeadline(d)}>
                      Snooze {d}d
                    </button>
                  ))}
                </span>
              </div>
            );
          })()}
          {canRequestCancel && (            <div className="sm:col-span-2">
              <button
                type="button"
                className="text-xs text-rose-600 underline underline-offset-2"
                data-testid="job-request-cancel-btn"
                onClick={() => { setCxlNote(""); setCxlMode("request"); setCxlOpen(true); }}
              >
                Request cancellation (needs the supplier's approval)
              </button>
            </div>
          )}
          {current.status === "waiting_prepay" && (
            <div className="sm:col-span-2 rounded-md border border-orange-200 bg-orange-50 text-orange-800 px-3 py-2 text-xs" data-testid="job-prepay-banner">
              <span className="font-semibold">Prepayment requested:</span> {current.prepay_amount != null ? fmtPrepay(current.prepay_amount) : "amount not specified — check the messages"}
              {current.prepay_percent ? ` (${current.prepay_percent}% of the agreed cost)` : ""}. Issue the payment and set the status to{" "}
              <span className="font-semibold">Prepay done</span>, or choose <span className="font-semibold">Prepay too high</span> to ask the supplier for a lower amount.
              {!isProviderUser && (
                <div className="mt-1.5">
                  <button type="button" className="underline font-semibold" data-testid="job-show-bank-btn" onClick={toggleBank}>
                    {bankOpen ? "Hide bank details" : "Show bank details"}
                  </button>
                </div>
              )}
              {bankOpen && bank && (
                <div className="mt-2 rounded border border-orange-200 bg-white/80 p-2 text-[11px] text-zinc-700 space-y-1" data-testid="job-bank-details">
                  {[
                    ["Account holder", bank.account_holder || bank.supplier_name],
                    ["IBAN", bank.iban],
                    ["BIC / SWIFT", bank.bic_swift],
                    ["Bank", bank.bank_name],
                    ["Account no.", bank.account_number],
                  ].filter(([, v]) => v).map(([k, v]) => (
                    <div key={k} className="flex justify-between gap-3"><span className="text-zinc-500">{k}</span><span className="font-mono font-medium text-right break-all">{v}</span></div>
                  ))}
                  {current.prepay_amount != null && (
                    <div className="flex justify-between gap-3"><span className="text-zinc-500">Amount</span><span className="font-mono font-medium">{fmtPrepay(current.prepay_amount)}</span></div>
                  )}
                  {bank.iban && (
                    <button
                      type="button"
                      className="underline text-orange-700 font-semibold"
                      data-testid="job-copy-iban"
                      onClick={() => { navigator.clipboard?.writeText(bank.iban); toast.success("IBAN copied"); }}
                    >
                      Copy IBAN
                    </button>
                  )}
                </div>
              )}
            </div>
          )}
          {current.status === "prepay_too_high" && (
            <div className="sm:col-span-2 rounded-md border border-rose-200 bg-rose-50 text-rose-800 px-3 py-2 text-xs" data-testid="job-prepay-too-high-banner">
              <span className="font-semibold">Prepayment too high:</span> you asked the supplier to adjust the requested prepayment
              {current.prepay_amount != null ? ` of ${fmtPrepay(current.prepay_amount)}` : ""}.
              {current.prepay_note && <> Your note: “{current.prepay_note}”.</>} Waiting for the supplier to re-request.
            </div>
          )}
          {current.prepay_paid && current.prepay_amount != null && (
            <Row label="Prepayment" value={<span className="text-teal-700 font-medium" data-testid="job-prepay-paid">{fmtPrepay(current.prepay_amount)} (paid — deducted on the final invoice)</span>} />
          )}
          {current.cancellation_fee?.amount != null && (
            <Row label="Cancellation fee" value={<span className="text-rose-700 font-medium" data-testid="job-cancellation-fee">{current.cancellation_fee.currency} {Number(current.cancellation_fee.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} (billed by the supplier)</span>} />
          )}
          {current.status === "dispute" && (
            <div className="sm:col-span-2 rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-xs" data-testid="job-dispute-banner">
              <span className="font-semibold">Disputed{current.dispute_count > 1 ? ` (round ${current.dispute_count})` : ""}:</span>{" "}
              {current.dispute_reason || "The completed work was not accepted."} The supplier has been notified and will set the job back to Completed after the rework.
              {(current.rework_deadline || "").slice(0, 10) && (
                <div className="mt-1" data-testid="job-rework-deadline">
                  <span className="font-semibold">Rework deadline:</span> {(current.rework_deadline || "").slice(0, 10)}
                  {reworkOverdue && (
                    <span className="ml-2 inline-flex items-center rounded-full bg-red-600 text-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide" data-testid="job-rework-overdue-badge">
                      Rework overdue
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
          <Row label="Priority" value={<PriorityPill v={current.priority} />} />
          <Row label="Deadline" value={(current.due_date || "").slice(0, 10) || "—"} />
          {current.start_date && <Row label="Work starts" value={(current.start_date || "").slice(0, 10)} />}
          {current.end_date && <Row label="Work ends" value={(current.end_date || "").slice(0, 10)} />}
          {startsAfterDeadline(current) && (
            <div className="sm:col-span-2 flex items-center gap-2 rounded-md border border-amber-300 bg-amber-50 text-amber-800 px-3 py-2 text-xs" data-testid="job-late-start-warning">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              The supplier's work-start date ({(current.start_date || "").slice(0, 10)}) is after your deadline ({(current.due_date || "").slice(0, 10)}). Consider moving the deadline or renegotiating the start.
            </div>
          )}
        </div>

        {/* Description */}
        {current.description && (
          <div className="mt-2">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Description</div>
            <div className="text-sm text-zinc-700 whitespace-pre-wrap">{current.description}</div>
          </div>
        )}

        {/* Scope — accepted supplier's numbered breakdown */}
        {acceptedQuote && ((acceptedQuote.scope_items || []).length > 0 || acceptedQuote.notes) && (
          <div className="mt-2 border-t border-zinc-200 pt-3">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Scope</div>
            {(acceptedQuote.scope_items || []).length > 0 && (
              <div className="rounded-md border border-emerald-100 bg-emerald-50/40 p-2" data-testid="job-detail-supplier-scope">
                <ul className="space-y-0.5">
                  {acceptedQuote.scope_items.map((s, i) => (
                    <li key={i} className="flex items-start justify-between gap-3 text-sm text-zinc-700">
                      <span className="min-w-0 whitespace-pre-wrap [overflow-wrap:anywhere]"><span className="text-zinc-400 font-medium mr-1">{i + 1}.</span>{s.text}</span>
                      {s.price != null && <span className="text-zinc-500 text-xs whitespace-nowrap shrink-0">{acceptedQuote.currency} {s.price}</span>}
                    </li>
                  ))}
                </ul>
                <VatBreakdown items={acceptedQuote.scope_items} amount={acceptedQuote.amount} currency={acceptedQuote.currency} vatPercent={acceptedQuote.vat_percent} testid="job-detail-vat" />
                <div className="mt-1 pt-1 border-t border-emerald-100 flex items-baseline justify-between text-sm">
                  <span className="text-zinc-500 text-xs">Agreed total{acceptedQuote.vat_percent != null ? " incl. VAT" : ""}</span>
                  <span className="font-medium text-zinc-800">{acceptedQuote.currency} {acceptedQuote.amount}</span>
                </div>
              </div>
            )}
            {acceptedQuote.notes && <p className="text-xs text-zinc-500 mt-1 whitespace-pre-wrap">{acceptedQuote.notes}</p>}
          </div>
        )}

        {/* Messages — thread with the accepted supplier (online suppliers only;
            offline/tracked-offline jobs are maintained by the yacht, no messaging) */}
        {acceptedQuote && !isProviderUser && acceptedQuote.supplier_kind === "marketplace" && !current.track_offline && !acceptedQuote.track_offline && (
          <div className="mt-2 border-t border-zinc-200 pt-3" data-testid="job-detail-messages">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Messages</div>
            <OwnerQuoteThread quote={acceptedQuote} onChanged={loadAcceptedQuote} />
          </div>
        )}

        {/* Parts */}
        {(current.parts_used?.length || 0) > 0 && (
          <div className="mt-4">
            <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-2">Parts used</div>
            <ul className="rounded-md border border-zinc-200 divide-y divide-zinc-100 bg-white">
              {current.parts_used.map((p, i) => {
                const inv = inventory.find((x) => x.id === p.item_id);
                return (
                  <li key={i} className="px-3 py-2 flex items-center justify-between text-sm">
                    <span>{p.item_name || inv?.name || p.item_id}</span>
                    <span className="font-mono text-zinc-600">{p.qty}</span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Before media */}
        <PhotoBucket
          title="Before Media"
          paths={current.before_photos || []}
          canAdd={can("maintenance:edit")}
          canDelete={can("maintenance:edit")}
          yacht_id={current.yacht_id}
          folder="maintenance/before"
          onUploaded={addBefore}
          onDelete={deleteBefore}
          testid="before"
        />

        {/* After media */}
        <PhotoBucket
          title="After Media"
          paths={current.after_photos || []}
          canAdd={canAddAfter}
          canDelete={canAddAfter}
          yacht_id={current.yacht_id}
          folder="maintenance/after"
          onUploaded={addAfter}
          onDelete={deleteAfter}
          testid="after"
          helper={
            isAssignedProvider
              ? "You're the assigned service provider — upload your completion photos here."
              : null
          }
        />

        {/* Documents — quotations + invoices + reports.
            Provider/assignee can attach; senior crew always can. */}
        <DocumentsBucket
          documents={current.documents || []}
          canAdd={canAddAfter}
          canRemoveAny={can("maintenance:edit")}
          userId={userId}
          yacht_id={current.yacht_id}
          onUploaded={addDocument}
          onRemove={removeDocument}
        />

        {/* History — below Documents (was a tab; user spec 2026-06) */}
        <div className="mt-2 border-t border-zinc-200 pt-3" data-testid="job-detail-history">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">History</div>
          <AuditTimeline rows={auditRows} emptyText="Actions on this job will show up here in chronological order." testidPrefix="job-audit" />
        </div>
        </>
        )}

        <DialogFooter className="flex flex-row-reverse sm:flex-row sm:justify-between gap-2">
          <Button variant="outline" onClick={onClose}>Close</Button>
          <div className="flex gap-2 flex-wrap">
            {tab === "details" && canEditThisJob && onEdit && (
              <Button
                variant="outline"
                onClick={async () => { await onEdit(); }}
                data-testid="job-detail-edit"
              >
                <Pencil className="h-4 w-4 mr-2" />Edit
              </Button>
            )}
            {tab === "details" && canCancelAccepted && (
              <Button
                variant="outline"
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                onClick={() => setCancelOpen(true)}
                data-testid="job-cancel-accepted"
              >
                <Ban className="h-4 w-4 mr-2" />Cancel job
              </Button>
            )}
            {tab === "details" && canDeleteThisJob && onDelete && (
              <Button
                variant="outline"
                className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                onClick={() => setConfirmDel(true)}
                data-testid="job-detail-delete"
              >
                <Trash2 className="h-4 w-4 mr-2" />{deleteLabel}
              </Button>
            )}
            {isDeleted && (
              <span className="text-xs text-zinc-500 italic self-center" data-testid="job-detail-deleted-badge">
                Deleted on {new Date(current.deleted_at).toLocaleDateString()}
                {current.deleted_by_name ? ` by ${current.deleted_by_name}` : ""}
              </span>
            )}
          </div>
        </DialogFooter>
      </DialogContent>

      {/* Destructive-action confirmation. Mounted as a sibling AlertDialog so
          it can portal above the Detail dialog without z-index collisions.
          Cancel is the default-focused button (radix behaviour) so an
          accidental Enter never deletes. */}
      <AlertDialog open={confirmDel} onOpenChange={(o) => { if (!o && !deleting) setConfirmDel(false); }}>
        <AlertDialogContent data-testid="job-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{deleteLabel}?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove "<strong>{current.title}</strong>" along with its
              parts list, before/after photos and attached documents. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {((current.quote_summary?.total || 0) > 0 || (current.unread_messages || 0) > 0) && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="job-delete-quote-warning">
              <span className="font-medium">Heads up — this task has an active supplier inquiry.</span>
              <ul className="mt-1 list-disc pl-5 text-[13px]">
                {(current.quote_summary?.total || 0) > 0 && (
                  <li>{current.quote_summary.total} supplier quote request{current.quote_summary.total > 1 ? "s" : ""}{current.quote_summary.quoted ? ` (${current.quote_summary.quoted} quoted)` : ""}</li>
                )}
                {(current.unread_messages || 0) > 0 && (
                  <li>{current.unread_messages} unread message{current.unread_messages > 1 ? "s" : ""} you haven't read yet</li>
                )}
              </ul>
              <p className="mt-1 text-[12px]">Deleting removes the inquiry and its messages for you and the supplier(s).</p>
            </div>
          )}
          {(offlineTask || internalTask) && current.invoice_id && (
            <label className="flex items-center gap-2 text-sm text-zinc-700 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 cursor-pointer" data-testid="job-delete-invoice-too">
              <input type="checkbox" checked={delInvToo} onChange={(e) => setDelInvToo(e.target.checked)} />
              Also delete the linked invoice
            </label>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} data-testid="job-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async (e) => {
                e.preventDefault();   // keep AlertDialog open until delete resolves
                if (deleting) return;
                setDeleting(true);
                try { await onDelete(delInvToo); }
                finally { setDeleting(false); setConfirmDel(false); }
              }}
              className="bg-red-600 hover:bg-red-700 text-white focus:ring-red-500"
              data-testid="job-delete-confirm-btn"
            >
              {deleting ? "Deleting…" : deleteLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel-accepted-job dialog: notify the supplier, then either reopen
          the task (pick another supplier) or cancel it completely. */}
      <Dialog open={cancelOpen} onOpenChange={(o) => { if (!o && !cancelling) setCancelOpen(false); }}>
        <DialogContent className="max-w-md [&>*]:min-w-0" data-testid="job-cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancel this job?</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-zinc-600">
              <strong>{current.assigned_supplier?.supplier_name}</strong> will be notified
              (in-app + email) that the yacht cancelled the accepted job.
            </p>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Reason (required — shared with the supplier)</div>
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={2}
                className="w-full rounded-md border border-input px-3 py-2 text-sm"
                placeholder="e.g. Schedule changed, work no longer needed…"
                data-testid="job-cancel-reason"
              />
            </div>
            <p className="text-zinc-600">What should happen to the task?</p>
          </div>
          <DialogFooter className="flex-col sm:flex-row sm:flex-wrap sm:justify-end gap-2">
            <Button variant="ghost" disabled={!!cancelling} onClick={() => setCancelOpen(false)} data-testid="job-cancel-back">
              Back
            </Button>
            <Button
              variant="outline"
              disabled={!!cancelling || !cancelReason.trim()}
              onClick={() => doCancelAccepted("reopen")}
              data-testid="job-cancel-reopen"
            >
              {cancelling === "reopen" ? "Cancelling…" : "Cancel & reopen task"}
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={!!cancelling || !cancelReason.trim()}
              onClick={() => doCancelAccepted("cancel")}
              data-testid="job-cancel-completely"
            >
              {cancelling === "cancel" ? "Cancelling…" : "Cancel task completely"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {findSup && (
        <FindSupplierDialog
          jobId={current.id}
          yachtId={current.yacht_id}
          defaultCategory={current.system_category}
          onClose={() => setFindSup(false)}
          onInvited={() => { setTab("quotes"); setQuotesKey((k) => k + 1); }}
        />
      )}

      {prepayDoneOpen && (
        <Dialog open onOpenChange={(o) => { if (!o) setPrepayDoneOpen(false); }}>
          <DialogContent className="max-w-md" data-testid="prepay-done-dialog">
            <DialogHeader>
              <DialogTitle>Mark prepayment done</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-zinc-600">
              Confirm you have paid the prepayment of <span className="font-semibold">{fmtPrepay(current.prepay_amount)}</span>.
              You can attach the transfer receipt — the supplier will see it in the job's documents.
            </p>
            {receiptDoc ? (
              <div className="flex items-center justify-between rounded-md border border-teal-200 bg-teal-50 px-3 py-2 text-xs text-teal-900" data-testid="prepay-receipt-attached">
                <span className="truncate min-w-0">Receipt: {receiptDoc.name}</span>
                <button type="button" className="text-teal-700 underline shrink-0 ml-2" onClick={() => setReceiptDoc(null)} data-testid="prepay-receipt-remove">remove</button>
              </div>
            ) : (
              <FileUploader
                yacht_id={current.yacht_id}
                folder="maintenance/documents"
                accept="application/pdf,image/*"
                label="Attach transfer receipt (optional)"
                testid="prepay-receipt-upload"
                onUploaded={(path, record) => setReceiptDoc({ path, name: record?.original_filename || "receipt" })}
              />
            )}
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setPrepayDoneOpen(false)} data-testid="prepay-done-cancel">Cancel</Button>
              <Button
                className="bg-teal-600 hover:bg-teal-700 text-white"
                onClick={async () => {
                  setPrepayDoneOpen(false);
                  try {
                    if (receiptDoc) {
                      await api.post(`/maintenance/${current.id}/documents`, { file_path: receiptDoc.path, name: `Prepayment receipt — ${receiptDoc.name}`, kind: "payment" });
                    }
                  } catch (e) { toast.error(fmtErr(e)); }
                  await saveStatus("prepay_done", null, { receipt_attached: !!receiptDoc });
                }}
                data-testid="prepay-done-confirm"
              >
                Mark as paid
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {prepayTooHighOpen && (
        <Dialog open onOpenChange={(o) => { if (!o) setPrepayTooHighOpen(false); }}>
          <DialogContent className="max-w-md" data-testid="prepay-too-high-dialog">
            <DialogHeader>
              <DialogTitle>Prepayment too high</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-zinc-600">
              The supplier will be notified and can adjust the amount{current.prepay_amount != null ? <> (currently <span className="font-semibold">{fmtPrepay(current.prepay_amount)}</span>)</> : ""} — the back-and-forth continues until you mark the prepayment done.
            </p>
            <textarea
              value={prepayNote}
              onChange={(e) => setPrepayNote(e.target.value)}
              rows={3}
              placeholder="Required note to the supplier — e.g. the amount or % you would accept"
              className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm outline-none focus:border-zinc-400"
              data-testid="prepay-too-high-note-input"
            />
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setPrepayTooHighOpen(false)} data-testid="prepay-too-high-cancel">Cancel</Button>
              <Button
                className="bg-rose-600 hover:bg-rose-700 text-white"
                disabled={!prepayNote.trim()}
                onClick={async () => { setPrepayTooHighOpen(false); await saveStatus("prepay_too_high", prepayNote); }}
                data-testid="prepay-too-high-confirm"
              >
                Send to supplier
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {disputeOpen && (
        <Dialog open onOpenChange={(o) => { if (!o) setDisputeOpen(false); }}>
          <DialogContent className="max-w-md" data-testid="dispute-dialog">
            <DialogHeader>
              <DialogTitle>Dispute completed work</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-zinc-600">
              The supplier will be notified, must fix the issue and mark the job completed again — the cycle repeats until you confirm.
            </p>
            <textarea
              value={disputeReason}
              onChange={(e) => setDisputeReason(e.target.value)}
              rows={3}
              placeholder="What is wrong with the work? (sent to the supplier)"
              className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm outline-none focus:border-zinc-400"
              data-testid="dispute-reason-input"
            />
            <div>
              <label className="text-xs font-medium text-zinc-600">Rework deadline (sent to the supplier — daily reminders until fixed)</label>
              <input
                type="date"
                value={reworkDeadline}
                min={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setReworkDeadline(e.target.value)}
                className="mt-1 w-full rounded-md border border-input bg-white px-3 py-2 text-sm outline-none focus:border-zinc-400"
                data-testid="dispute-deadline-input"
              />
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button variant="outline" onClick={() => setDisputeOpen(false)} data-testid="dispute-cancel">Cancel</Button>
              <Button
                className="bg-red-600 hover:bg-red-700 text-white"
                disabled={!disputeReason.trim()}
                onClick={async () => { setDisputeOpen(false); await saveStatus("dispute", disputeReason, { rework_deadline: reworkDeadline || null }); }}
                data-testid="dispute-confirm"
              >
                Dispute job
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}
